import { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Minus, Plus, Trash2, CreditCard, ShoppingBag, Truck } from "lucide-react";
import { useCart } from "@/hooks/useCart";
import { useShippingConfig } from "@/hooks/useShippingConfig";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { checkoutSchema } from "@/lib/checkoutValidation";
import brandLogo from "@/assets/regard-de-nature-logo.png";
import { BRAND } from "@/config/brand";

interface CartDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CartDrawer({ open, onOpenChange }: CartDrawerProps) {
  const shippingConfigQuery = useShippingConfig();
  const shippingConfig = shippingConfigQuery.data;
  const { cartItems, updateQuantity, removeFromCart, clearCart, subtotal, taxAmount, total, shippingFee, isFreeShipping, amountUntilFreeShipping, freeShippingThreshold, refreshCart, loading: cartLoading } = useCart(shippingConfig);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    billingAddress: "",
    shippingAddress: "",
    sameAddress: true,
  });

  useEffect(() => {
    if (open) void refreshCart();
  }, [open, refreshCart]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (cartItems.length === 0) {
      toast.error("Votre panier est vide");
      return;
    }

    // Validate form data with Zod
    const validationResult = checkoutSchema.safeParse(formData);
    if (!validationResult.success) {
      const firstError = validationResult.error.errors[0];
      toast.error(firstError.message);
      return;
    }

    setLoading(true);

    try {
      // Prepare cart items for checkout
      const checkoutItems = cartItems.map(item => ({
        product_id: item.product_id,
        product_title: item.product.title,
        quantity: item.quantity,
        unit_price: item.product.price,
      }));

      // Get session ID from localStorage
      const sessionId = localStorage.getItem("regard_de_nature_cart_session") || "";

      // Call Stripe checkout function
      const { data, error } = await supabase.functions.invoke("create-product-checkout", {
        body: {
          cartItems: checkoutItems,
          customerInfo: {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            phone: formData.phone,
            billingAddress: formData.billingAddress,
            shippingAddress: formData.sameAddress ? formData.billingAddress : formData.shippingAddress,
          },
          sessionId,
        },
      });

      if (error) throw error;
      if (!data?.url) throw new Error("No checkout URL returned");

      // Redirect to Stripe Checkout
      window.open(data.url, "_blank");
      toast.success("Redirection vers le paiement...");
      onOpenChange(false);
    } catch (error: any) {
      console.error("Error creating checkout:", error);
      toast.error("Erreur lors de la création du paiement");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg">
        <SheetHeader>
          <div className="flex flex-col items-center mb-4">
            <img src={brandLogo} alt={BRAND.name.full} className="w-12 h-12 mb-2" />
            <SheetTitle className="text-primary">Votre Panier</SheetTitle>
            <SheetDescription className="text-center">
              {BRAND.name.full} - {BRAND.baseline.full}
            </SheetDescription>
          </div>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-200px)] pr-4">
          {cartItems.length === 0 && cartLoading ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <ShoppingBag className="w-16 h-16 mb-4 opacity-50 animate-pulse" />
              <p>Chargement du panier...</p>
            </div>
          ) : cartItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <ShoppingBag className="w-16 h-16 mb-4 opacity-50" />
              <p>Votre panier est vide</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Cart Items */}
              <div className="space-y-4">
                {cartItems.map((item) => (
                  <div key={item.id} className="flex gap-4 p-4 border rounded-lg">
                    <div className="flex-1 space-y-2">
                      <h4 className="font-medium text-sm">{item.product.title}</h4>
                      <p className="text-xs text-muted-foreground line-clamp-2">
                        {item.product.short_description}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-secondary">
                          {item.product.price}€
                        </span>
                        <div className="flex items-center gap-2">
                          <Button
                            size="icon"
                            variant="outline"
                            className="h-8 w-8"
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            aria-label={`Réduire la quantité de ${item.product.title}`}
                          >
                            <Minus className="h-3 w-3" aria-hidden="true" />
                          </Button>
                          <span className="w-8 text-center" aria-label={`Quantité: ${item.quantity}`}>{item.quantity}</span>
                          <Button
                            size="icon"
                            variant="outline"
                            className="h-8 w-8"
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            aria-label={`Augmenter la quantité de ${item.product.title}`}
                          >
                            <Plus className="h-3 w-3" aria-hidden="true" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-destructive"
                            onClick={() => removeFromCart(item.id)}
                            aria-label={`Supprimer ${item.product.title} du panier`}
                          >
                            <Trash2 className="h-3 w-3" aria-hidden="true" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <Separator />

              {/* Shipping message */}
              {cartItems.length > 0 && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-secondary/10 text-sm">
                  <Truck className="h-4 w-4 text-secondary shrink-0" />
                  {isFreeShipping ? (
                    <span className="text-secondary font-medium">🎉 Livraison offerte !</span>
                  ) : (
                    <span className="text-muted-foreground">
                      Plus que <strong className="text-secondary">{amountUntilFreeShipping}€</strong> pour bénéficier de la livraison offerte
                    </span>
                  )}
                </div>
              )}

              {/* Order Summary */}
              <div className="space-y-2 bg-muted/30 p-4 rounded-lg">
                <div className="flex justify-between text-sm">
                  <span>Sous-total HT</span>
                  <span>{subtotal}€</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>TVA (20%)</span>
                  <span>{taxAmount}€</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Frais de livraison</span>
                  {isFreeShipping ? (
                    <span className="text-secondary font-medium">Offerts</span>
                  ) : (
                    <span>{shippingFee}€</span>
                  )}
                </div>
                <Separator />
                <div className="flex justify-between font-bold text-lg">
                  <span>Total TTC</span>
                  <span className="text-secondary">{total}€</span>
                </div>
              </div>

              <Separator />

              {/* Customer Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">Prénom *</Label>
                    <Input
                      id="firstName"
                      value={formData.firstName}
                      onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Nom *</Label>
                    <Input
                      id="lastName"
                      value={formData.lastName}
                      onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Téléphone *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="billingAddress">Adresse de facturation *</Label>
                  <Input
                    id="billingAddress"
                    value={formData.billingAddress}
                    onChange={(e) => setFormData({...formData, billingAddress: e.target.value})}
                    required
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="sameAddress"
                    checked={formData.sameAddress}
                    onCheckedChange={(checked) => 
                      setFormData({...formData, sameAddress: checked as boolean})
                    }
                  />
                  <Label htmlFor="sameAddress" className="text-sm">
                    Adresse de livraison identique
                  </Label>
                </div>

                {!formData.sameAddress && (
                  <div className="space-y-2">
                    <Label htmlFor="shippingAddress">Adresse de livraison *</Label>
                    <Input
                      id="shippingAddress"
                      value={formData.shippingAddress}
                      onChange={(e) => setFormData({...formData, shippingAddress: e.target.value})}
                      required={!formData.sameAddress}
                    />
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground"
                  disabled={loading}
                >
                  <CreditCard className="mr-2 h-4 w-4" />
                  {loading ? "Traitement..." : "Valider la commande et payer"}
                </Button>

                <p className="text-xs text-center text-muted-foreground">
                  Paiement sécurisé par Stripe
                </p>
              </form>
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
